/**
 * The property page's site-history rail: what PazNetworks delivered here, what
 * is scheduled, and what is happening right now, in one chronology.
 *
 * Pure, and out of the page component, because the ordering rules are easy to
 * get subtly wrong and impossible to test inline. One already was: a curated
 * visit dated in the future was labelled "Delivered", because the label followed
 * the source rather than the date. Nothing caught it but a pair of eyes.
 *
 * The rules, in order:
 *   • a label follows the date, never the source — the visit catalog carries
 *     planned coverage as well as history;
 *   • what is happening now sorts first, then what is next soonest-first, then
 *     history most-recent-first. A reader's question is "is anyone here, and
 *     when are they coming back", not "what happened in chronological order";
 *   • the list is capped, because a property with forty recorded visits should
 *     not push everything else off the page.
 */

export type TimelineTone = 'past' | 'current' | 'future' | 'planned';

/** Labels are passed in already translated; this module owns no copy. */
export interface TimelineLabels {
  delivered: string;
  scheduled: string;
  onSite: string;
}

export interface VisitRecord {
  id: string;
  /** Curated label as operations wrote it: "Apr 1–3, 2026". */
  windowLabel: string;
  title: string;
  /** ISO yyyy-mm-dd. */
  occurredOn: string;
}

export interface TripRecord {
  id: string;
  project_number: string;
  scope: string | null;
  start_date: string | null;
  end_date: string | null;
}

export interface SiteTimelineEntry {
  id: string;
  when: string;
  title: string;
  kindLabel: string;
  tone: TimelineTone;
  detail?: string | null;
}

export const TIMELINE_LIMIT = 12;

/**
 * @param formatDate renders a trip's ISO date for display; injected so this
 *   module stays free of locale concerns.
 * @param todayIso the day to reckon against, as yyyy-mm-dd.
 */
export function buildSiteTimeline({
  visits,
  trips,
  labels,
  formatDate,
  todayIso,
  limit = TIMELINE_LIMIT,
}: {
  visits: VisitRecord[];
  trips: TripRecord[];
  labels: TimelineLabels;
  formatDate: (iso: string) => string;
  todayIso: string;
  limit?: number;
}): SiteTimelineEntry[] {
  type Dated = SiteTimelineEntry & { anchor: string };

  const fromVisits: Dated[] = visits.map((visit) => {
    const upcoming = visit.occurredOn > todayIso;
    return {
      id: `visit:${visit.id}`,
      anchor: visit.occurredOn,
      when: visit.windowLabel,
      title: visit.title,
      kindLabel: upcoming ? labels.scheduled : labels.delivered,
      tone: upcoming ? 'future' : 'past',
    };
  });

  const fromTrips: Dated[] = trips.map((trip) => {
    const start = trip.start_date ?? '';
    const end = trip.end_date ?? start;
    const current = !!start && start <= todayIso && end >= todayIso;
    return {
      id: `trip:${trip.id}`,
      anchor: start || todayIso,
      when: start ? formatDate(start) : trip.project_number,
      title: trip.scope?.trim() || trip.project_number,
      kindLabel: current ? labels.onSite : labels.scheduled,
      tone: current ? 'current' : end >= todayIso ? 'future' : 'past',
      detail: trip.project_number,
    };
  });

  const rank = (entry: Dated) => (entry.tone === 'current' ? 0 : entry.anchor >= todayIso ? 1 : 2);

  return [...fromTrips, ...fromVisits]
    .sort((a, b) => {
      const byRank = rank(a) - rank(b);
      if (byRank !== 0) return byRank;
      // Upcoming reads soonest-first; history reads most-recent-first.
      return rank(a) === 2 ? b.anchor.localeCompare(a.anchor) : a.anchor.localeCompare(b.anchor);
    })
    .slice(0, limit)
    .map(({ anchor: _anchor, ...entry }) => entry);
}
